/**
 * Upsell suggestion computation.
 *
 * Compares the user's current individual subscription spend against
 * available bundles to find savings opportunities.
 */
/**
 * Compute the best upsell suggestion, or null if none applies.
 *
 * Rules:
 * 1. If user already has a bundle (ecosystem subscription) → null.
 * 2. Sum monthly cost of all active individual product subscriptions.
 *    - For each product sub, look up the catalog tier's monthlyPriceCents.
 *    - Skip free-tier products (no cost to compare).
 * 3. For each visible bundle, compare its monthly price to the user's spend.
 * 4. Return the bundle with the highest savings.
 * 5. If no bundle is strictly cheaper but user has 2+ paid products,
 *    still suggest the cheapest bundle (convenience upsell, savings ≥ 0).
 */
export function computeUpsell(catalog, subscription) {
    // Already on a bundle — no upsell needed
    if (subscription.ecosystem)
        return null;
    // Build a lookup: productId → catalog product
    const productMap = new Map(catalog.products.map((p) => [p.productId, p]));
    // Sum the user's current monthly cost from individual subscriptions
    let currentMonthlyCostCents = 0;
    let paidProductCount = 0;
    for (const sub of subscription.products) {
        if (sub.status !== "active" && sub.status !== "trialing")
            continue;
        if (sub.tier === "free")
            continue;
        const catalogProduct = productMap.get(sub.productId);
        if (!catalogProduct)
            continue;
        const tierInfo = catalogProduct.tiers[sub.tier];
        if (tierInfo) {
            currentMonthlyCostCents += tierInfo.monthlyPriceCents;
            paidProductCount++;
        }
    }
    // Need at least 1 paid product for an upsell to be relevant
    if (paidProductCount === 0)
        return null;
    // Evaluate each visible bundle
    let bestUpsell = null;
    for (const bundle of catalog.bundles) {
        if (!bundle.visible)
            continue;
        const bundleMonthlyCostCents = bundle.monthlyPriceCents;
        const savingsCents = currentMonthlyCostCents - bundleMonthlyCostCents;
        // Strict savings: bundle is cheaper
        // Convenience upsell: 2+ paid products, bundle consolidates them
        const worthSuggesting = savingsCents > 0 || (paidProductCount >= 2 && savingsCents >= 0);
        if (!worthSuggesting)
            continue;
        const savingsPercent = currentMonthlyCostCents > 0
            ? Math.round((savingsCents / currentMonthlyCostCents) * 100)
            : 0;
        const candidate = {
            bundle,
            currentMonthlyCostCents,
            bundleMonthlyCostCents,
            savingsCents,
            savingsPercent,
        };
        // Pick the bundle with the highest absolute savings
        if (!bestUpsell || savingsCents > bestUpsell.savingsCents) {
            bestUpsell = candidate;
        }
    }
    return bestUpsell;
}
